import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Megaphone, Plus, Edit2, Trash2, X, Loader2, ArrowUp, ArrowDown,
  Archive, RotateCcw, Eye, EyeOff, Clock,
} from "lucide-react";

const EMPTY = {
  title_ar: "", title_en: "", desc_ar: "", desc_en: "",
  total_slots: 10, filled_slots: 0, active: true,
  show_seat_count: true,
  status: "active", display_order: 0,
  regulatory_frameworks: "",
};

export default function AdminCampaigns() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get("/admin/campaigns").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const visible = useMemo(() => items.filter((c) => showArchived ? true : !c.is_archived), [items, showArchived]);

  const save = async () => {
    if (!editing.title_en || !editing.title_ar) { toast.error("Titles required"); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/campaigns/${editing.id}`, editing);
      else await api.post("/admin/campaigns", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const setStatus = async (c, status) => { try { await api.put(`/admin/campaigns/${c.id}/status`, { status }); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const archive = async (c) => { if (window.confirm(`Archive "${c.title_en}"?`)) { await api.post(`/admin/campaigns/${c.id}/archive`); load(); } };
  const restore = async (c) => { await api.post(`/admin/campaigns/${c.id}/restore`); load(); };
  const remove = async (c) => {
    if (!window.confirm("Delete permanently?")) return;
    try {
      await api.delete(`/admin/campaigns/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const move = async (c, dir) => {
    const list = [...visible].sort((a, b) => a.display_order - b.display_order);
    const idx = list.findIndex((x) => x.id === c.id);
    const t = idx + dir;
    if (t < 0 || t >= list.length) return;
    [list[idx], list[t]] = [list[t], list[idx]];
    await api.put("/admin/campaigns/reorder", { ordered_ids: list.map((x) => x.id) });
    load();
  };

  const badge = (c) =>
    c.is_archived ? <Badge className="bg-navy/20 text-navy/70"><Archive size={11} className="inline me-1" />Archived</Badge>
    : c.status === "coming_soon" ? <Badge className="bg-amber-100 text-amber-800"><Clock size={11} className="inline me-1" />Coming Soon</Badge>
    : c.status === "hidden" ? <Badge className="bg-navy/20 text-navy/70"><EyeOff size={11} className="inline me-1" />Hidden</Badge>
    : <Badge className="bg-emerald-100 text-emerald-800"><Eye size={11} className="inline me-1" />Active</Badge>;

  return (
    <div data-testid="admin-campaigns">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <span className="eyebrow">Limited-slot cohorts</span>
          <h1 className="mt-2 text-3xl text-navy flex items-center gap-3"><Megaphone size={26} className="text-gold" /> Campaigns</h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-navy/70 inline-flex items-center gap-2">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="camp-show-archived" /> Show archived
          </label>
          <button onClick={() => setEditing({ ...EMPTY, display_order: items.length })} className="btn-gold" data-testid="camp-add"><Plus size={14} /> New</button>
        </div>
      </div>
      <span className="divider-gold" />

      <div className="card-elev divide-y divide-navy/10">
        {visible.length === 0 ? (
          <div className="p-8 text-center text-navy/50">No campaigns.</div>
        ) : visible.sort((a, b) => a.display_order - b.display_order).map((c) => (
          <div key={c.id} className="p-5 flex items-center gap-4" data-testid={`camp-row-${c.id}`}>
            <div className="flex flex-col gap-0.5">
              <button onClick={() => move(c, -1)} className="text-navy/50 hover:text-gold" data-testid={`camp-up-${c.id}`}><ArrowUp size={12} /></button>
              <button onClick={() => move(c, +1)} className="text-navy/50 hover:text-gold" data-testid={`camp-down-${c.id}`}><ArrowDown size={12} /></button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-navy font-medium">{c.title_en}</div>
              <div className="text-xs text-navy/60">{c.title_ar} · {c.filled_slots}/{c.total_slots} seats</div>
            </div>
            {badge(c)}
            {!c.is_archived && (
              <select value={c.status || "active"} onChange={(e) => setStatus(c, e.target.value)} className="text-xs px-2 py-1 border border-navy/20 rounded-sm bg-white" data-testid={`camp-status-${c.id}`}>
                <option value="active">Active</option>
                <option value="coming_soon">Coming Soon</option>
                <option value="hidden">Hidden</option>
              </select>
            )}
            <button onClick={() => setEditing({ ...EMPTY, ...c })} className="text-navy/70 p-1.5" data-testid={`camp-edit-${c.id}`}><Edit2 size={14} /></button>
            {c.is_archived
              ? <button onClick={() => restore(c)} className="text-emerald-700 p-1.5" data-testid={`camp-restore-${c.id}`}><RotateCcw size={14} /></button>
              : <button onClick={() => archive(c)} className="text-amber-700 p-1.5" data-testid={`camp-archive-${c.id}`}><Archive size={14} /></button>}
            <button onClick={() => remove(c)} className="text-red-600/80 p-1.5" data-testid={`camp-del-${c.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-3xl mx-auto rounded-md border border-gold/30 p-8 my-6" onClick={(e) => e.stopPropagation()} data-testid="camp-editor">
            <div className="flex justify-between">
              <h2 className="text-2xl text-navy">{editing.id ? "Edit campaign" : "New campaign"}</h2>
              <button onClick={() => setEditing(null)} className="text-navy/50"><X size={18} /></button>
            </div>
            <span className="divider-gold" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input placeholder="Title (AR)" value={editing.title_ar} onChange={(e) => setEditing({ ...editing, title_ar: e.target.value })} data-testid="camp-title-ar" />
              <Input placeholder="Title (EN)" value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} data-testid="camp-title-en" />
              <Textarea placeholder="Description (AR)" value={editing.desc_ar} onChange={(e) => setEditing({ ...editing, desc_ar: e.target.value })} data-testid="camp-desc-ar" />
              <Textarea placeholder="Description (EN)" value={editing.desc_en} onChange={(e) => setEditing({ ...editing, desc_en: e.target.value })} data-testid="camp-desc-en" />
              <Input type="number" placeholder="Total slots" value={editing.total_slots} onChange={(e) => setEditing({ ...editing, total_slots: parseInt(e.target.value || "0", 10) })} data-testid="camp-total" />
              <Input type="number" placeholder="Filled slots" value={editing.filled_slots} onChange={(e) => setEditing({ ...editing, filled_slots: parseInt(e.target.value || "0", 10) })} data-testid="camp-filled" />
              <label className="flex items-center gap-2 text-navy md:col-span-2">
                <input type="checkbox" className="w-5 h-5 accent-gold"
                       checked={editing.show_seat_count !== false}
                       onChange={(e) => setEditing({ ...editing, show_seat_count: e.target.checked })}
                       data-testid="camp-show-seats" />
                Show seat count publicly
                <span className="text-xs text-navy/50 ms-2">
                  (hide to remove the “X seats left” badge and progress bar from the public page)
                </span>
              </label>
              <select className="field-box" value={editing.status || "active"} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                <option value="active">Active</option>
                <option value="coming_soon">Coming Soon (قريباً)</option>
                <option value="hidden">Hidden</option>
              </select>
              <Input type="number" placeholder="Display order" value={editing.display_order} onChange={(e) => setEditing({ ...editing, display_order: parseInt(e.target.value || "0", 10) })} />
              <div className="md:col-span-2">
                <label className="text-xs text-navy/70 mb-1 block">
                  Regulatory frameworks (CSV — e.g. <code>pdpl,gdpr</code> or <code>🇸🇾 Syrian Data Protection</code>)
                </label>
                <input className="field-box w-full"
                       placeholder="pdpl,gdpr,uae_pdpl,qatar_pdp"
                       value={editing.regulatory_frameworks || ""}
                       onChange={(e) => setEditing({ ...editing, regulatory_frameworks: e.target.value })}
                       data-testid="camp-reg" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} className="btn-gold" data-testid="camp-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
              </button>
              <button onClick={() => setEditing(null)} className="btn-outline-navy">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
